import { useState } from "react";
import { motion } from "framer-motion";
import { Navigation } from "./Index/components/Navigation";
import { Footer } from "./Index/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { OpenHouseBanner } from "@/components/OpenHouseBanner";
import { openWhatsApp, WHATSAPP_MESSAGES } from "@/lib/whatsapp";
import { PageCTASection } from "@/components/PageCTASection";
import { OptimizedPicture } from "@/components/OptimizedPicture";
import { usePageMeta } from "@/hooks/usePageMeta";
import { YouTubeVideo } from "@/components/YouTubeVideo";

const META: Record<string, { title: string; description: string }> = {
  es: {
    title: "Quiénes somos – Nuestra historia | Gándara International School",
    description: "Fundado en 2013 por dos madres que buscaban una educación diferente. Colegio internacional autorizado por la Xunta y acreditado por NEASC en Gondomar.",
  },
  en: {
    title: "About Us – Our Story | Gándara International School",
    description: "Founded in 2013 by two mothers seeking a different education. International school authorized by the Xunta and NEASC accredited in Gondomar.",
  },
  pt: {
    title: "Sobre Nós – A Nossa História | Gándara International School",
    description: "Fundada em 2013 por duas mães que procuravam uma educação diferente. Escola internacional autorizada pela Xunta e acreditada pela NEASC em Gondomar.",
  },
  fr: {
    title: "Qui Sommes-Nous – Notre Histoire | Gándara International School",
    description: "Fondée en 2013 par deux mères à la recherche d'une éducation différente. École internationale autorisée par la Xunta et accréditée NEASC à Gondomar.",
  },
};

// Fotos del equipo por nombre (independiente del orden del array)
// Convierte la palabra "Barkibu" de las bios en un enlace a barkibu.com
const linkifyBarkibu = (text: string) => {
  const parts = text.split("Barkibu");
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <span key={i}>
      {i > 0 && (
        <a
          href="https://www.barkibu.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary font-medium underline underline-offset-2"
        >
          Barkibu
        </a>
      )}
      {part}
    </span>
  ));
};

const TEAM_PHOTOS: Record<string, string> = {
  "Iago Ferraz": "/lovable-uploads/6652f579-510b-437b-956f-3d5af6141cc2.png",
  "Verónica Álvarez": "/lovable-uploads/f974b8af-66a1-4747-8040-cfca729740e7.png",
  "Patricia Iglesias": "/lovable-uploads/f77b89bd-9d0e-4054-88ac-52fba2fb5ad5.png",
  "Sandra Amigo": "/lovable-uploads/sandra-amigo.png",
  "David Torres": "/lovable-uploads/david-torres.jpg",
  "Pablo Pazos": "/lovable-uploads/pablo-pazos.jpg",
  "Carmen Aguilar": "/lovable-uploads/carmen-aguilar.jpg",
};

// Encuadre del recorte 4:3 por foto (las verticales con la cara arriba se alinean al techo)
const TEAM_PHOTO_POS: Record<string, string> = {
  "Pablo Pazos": "object-top",
  "Patricia Iglesias": "object-top",
};

const About = () => {
  const { language } = useLanguage();
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const meta = META[language] || META.es;
  usePageMeta({ title: meta.title, description: meta.description });

  const handleBookVisit = () => {
    openWhatsApp(WHATSAPP_MESSAGES.bookVisit[language]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <OpenHouseBanner onVisibilityChange={setIsBannerVisible} />
      
      <div className={`container mx-auto px-4 ${isBannerVisible ? 'pt-[120px]' : 'pt-20'} pb-20`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-16">
            <h1 className="text-5xl font-light text-primary mb-6">
              {translations[language].about.title}
            </h1>
            <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">
              {translations[language].about.subtitle}
            </p>
            <div className="flex justify-center mb-12">
              <Button
                onClick={handleBookVisit}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 rounded-lg text-lg shadow-lg transform transition-all duration-200 hover:scale-105 hover:shadow-xl flex items-center gap-3"
              >
                <MessageCircle className="w-5 h-5" />
                {translations[language].about.bookVisit}
              </Button>
            </div>
          </div>

          {/* Story Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-light mb-6 text-primary">
              {translations[language].about.story.title}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {translations[language].about.story.content}
            </p>
          </section>

          {/* Mission Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-light mb-6 text-primary">
              {translations[language].about.mission.title}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {translations[language].about.mission.content}
            </p>
          </section>

          {/* Values Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-light mb-8 text-primary text-center">
              {translations[language].about.values.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {translations[language].about.values.items.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card rounded-xl p-6 border shadow-sm"
                >
                  <h3 className="text-xl font-medium text-primary mb-2">{value.title}</h3>
                  <p className="text-muted-foreground">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Team Section */}
          <section>
            <h2 className="text-3xl font-light mb-12 text-primary text-center">
              {translations[language].about.teamTitle}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {translations[language].about.team.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-shadow border overflow-hidden"
                >
                  <div className="aspect-[4/3] w-full overflow-hidden bg-gradient-to-br from-primary/10 to-accent/10">
                    {TEAM_PHOTOS[member.name] ? (
                      <OptimizedPicture 
                        src={TEAM_PHOTOS[member.name]}
                        alt={member.name}
                        width={400}
                        height={300}
                        loading="lazy"
                        decoding="async"
                        className={`w-full h-full object-cover ${TEAM_PHOTO_POS[member.name] || ""}`}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-primary/30 rounded-full mb-2 mx-auto flex items-center justify-center">
                            <span className="text-2xl font-medium text-primary/60">
                              {member.name.charAt(0)}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-primary/60">
                            {translations[language].about.photoComingSoon}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-8">
                    <h3 className="text-xl font-medium text-primary mb-2">{member.name}</h3>
                    <h4 className="text-primary font-medium mb-4">{member.role}</h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">{linkifyBarkibu(member.description)}</p>
                  </div>
                </motion.div>
              ))}
            </div>

                      {/* Video Section */}
          <section className="mt-16 mb-16">
            <h2 className="text-3xl font-light mb-8 text-primary text-center">
              {translations[language].about.video.title}
            </h2>
            <div className="aspect-video rounded-2xl overflow-hidden shadow-lg">
              <YouTubeVideo id="0-yMdRgMNfw" title={translations[language].about.video.title} />
            </div>
          </section>

          <PageCTASection />
          </section>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default About;
